import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import { todayKey } from "@/lib/time";
import { Panel, PageHeader, StatusPill, Loading } from "@/components/portal/widgets";
import { Info, Repeat, AlertTriangle } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const today = todayKey;

function activeDays(so) {
  const raw = so.days || so.weekdays || so.pattern || so.dow;
  if (Array.isArray(raw)) {
    return DAYS.map((_, i) => raw.includes(i) || raw.includes(String(i)) || raw.includes(DAYS[i]) || raw.map?.((x) => String(x).toLowerCase()).includes(DAYS[i].toLowerCase()));
  }
  if (raw && typeof raw === "object") return DAYS.map((_, i) => !!(raw[i] || raw[String(i)]));
  return DAYS.map(() => false);
}

export default function StandingOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [missing, setMissing] = useState([]);
  const [date] = useState(today());

  useEffect(() => {
    Promise.all([
      fleetApi.get("/trips/standing-orders"),
      fleetApi.get(`/trips/standing-orders/alerts?date=${date}`).catch(() => ({ data: {} })),
    ]).then(([o, a]) => {
      setOrders(Array.isArray(o.data) ? o.data : o.data?.standing_orders || []);
      setMissing(a.data?.missing || a.data?.alerts || []);
    }).catch(() => setOrders([]));
  }, [date]);

  if (!orders) return <Loading />;

  return (
    <div data-testid="standing-orders-page">
      <PageHeader title="Standing Orders" subtitle={`Recurring riders · ${orders.length} on file`} />

      <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-slate-800 bg-blue-500/5 px-4 py-2.5 text-xs text-slate-400" data-testid="standing-note">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
        <span>Riders who travel on a regular weekly pattern. The alert below flags regulars who have no trip scheduled today so dispatchers can follow up.</span>
      </div>

      {missing.length > 0 && (
        <Panel className="mb-6" title="Regulars missing today" testid="standing-missing"
          action={<span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono-data text-xs font-bold text-amber-400">{missing.length}</span>}>
          <div className="divide-y divide-slate-800">
            {missing.map((m, i) => (
              <div key={m.id || i} className="flex items-center justify-between px-5 py-3" data-testid={`standing-missing-${i}`}>
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="font-semibold text-white">{m.rider_name || m.name || "Rider"}</span>
                  <span className="text-slate-500">· usually {m.usual_pickup || m.pickup_address || "scheduled"}</span>
                </div>
                <StatusPill value="missing" />
              </div>
            ))}
          </div>
        </Panel>
      )}

      {orders.length === 0 ? (
        <Panel testid="standing-empty">
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400"><Repeat className="h-7 w-7" /></span>
            <p className="font-semibold text-white">No standing orders yet</p>
            <p className="max-w-md text-sm text-slate-500">When riders travel on a recurring weekly schedule, they'll appear here with their day pattern — making it easy to spot regulars who are missing from a day's manifest.</p>
          </div>
        </Panel>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="standing-grid">
          {orders.map((so) => {
            const days = activeDays(so);
            return (
              <div key={so.id} className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid={`standing-card-${so.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white">{so.rider_name || so.name}</div>
                  {so.driver_name && (
                    <button onClick={() => so.driver_id && navigate(`/portal/drivers/${so.driver_id}`)}
                      className="font-mono-data text-[11px] text-blue-300 hover:underline">{so.driver_name}</button>
                  )}
                </div>
                <div className="mt-1 truncate text-xs text-slate-500">{so.pickup_address} → {so.dropoff_name || so.dropoff_address}</div>
                <div className="mt-3 flex gap-1">
                  {DAYS.map((d, i) => (
                    <span key={d} className={`flex h-6 w-8 items-center justify-center rounded font-mono-data text-[10px] font-semibold ${days[i] ? "bg-blue-600/20 text-blue-300" : "bg-slate-800/60 text-slate-600"}`}>{d[0]}</span>
                  ))}
                </div>
                {so.pickup_time && <div className="mt-2 font-mono-data text-[11px] text-slate-500">Usual pickup: {so.pickup_time}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
